// utils.js
import storageClient from "./storageClient";
import apiClient from './apiClient';
import { fetchAuthSession } from "@aws-amplify/auth"; // Import the correct method
import { AudioSeparationPath, AudioUploadPath, MbSize } from "const";
import { updateSeparation } from "./redux/slices/AudioFilesSlice";
import { signIn } from "./redux/slices/UserSlice";
import slugify from "slugify";
import axios from "axios";

// File drag/drops and AWS disagree on what to call the file name.  ;)
export function GetFileName(file) {
  return file.metadata?.title || file.name || file.key;
}

export function removeFileExtension(filename) {
  if (!filename || typeof filename !== "string") {
    return filename; // Return input if it's empty, null, or not a string
  }

  const lastDotIndex = filename.lastIndexOf(".");
  if (lastDotIndex === -1) {
    return filename; // No extension found
  }
  return filename.substring(0, lastDotIndex);
}

export function GetFileNameFromPath(filePath) {
  const startIndex = filePath.lastIndexOf("/") + 1;
  return filePath.substring(startIndex, filePath.length);
}

function assertMapHasKey(parameterMap, requiredKey) {
  if (
    typeof parameterMap !== "object" ||
    parameterMap === null ||
    Array.isArray(parameterMap)
  ) {
    throw new Error("Parameter must be a non-null object.");
  }

  if (!parameterMap.hasOwnProperty(requiredKey)) {
    throw new Error(`Parameter must contain the key "${requiredKey}".`);
  }
}

export async function SignInUser(dispatch) {
  try {

    const userEmail = await getCurrentUserEmail();
    const sub = await getCurrentUserSub();

    if (!userEmail) {
      throw new Error("User email is not available.");
    }

    const postData = {
      identityEmail: userEmail,
    };
 
    const response = await apiClient.handlePost({
      apiName: "soundshatterapi",
      path: `/users/${sub}/auth/signin`,
      body: postData,
    });

    // Dispatch signin response to Redux user slice.
    dispatch(
      signIn({
        key: sub,
        user: response.body,
      })
    );

    return;
  } catch (error) {
    console.log("SignInUser Error:", JSON.stringify(error));
    throw error;
  }
}

export function getLogicalKey(file) {
  const extIndex = file.name.lastIndexOf(".");
  const fileExtension = file.name.substring(extIndex);
  return `${AudioUploadPath}${slugify(file?.metadata?.artist)}/${slugify(file?.metadata?.album)}/${slugify(file?.metadata?.title)}${fileExtension}`;
}

export function GetSeparationKey(metadata) {
  assertMapHasKey(metadata, "artist");
  assertMapHasKey(metadata, "album");
  assertMapHasKey(metadata, "title");

  return `${AudioSeparationPath}${slugify(metadata?.artist)}/${slugify(metadata?.album)}/${slugify(metadata?.title)}`;
}

export async function getIdToken() {
  try {
    const session = await fetchAuthSession();
    const idToken = session?.tokens?.idToken?.toString?.();

    return typeof idToken === "string" && idToken.length > 0 ? idToken : null;

  } catch (error) {
    console.warn("Failed to fetch ID token:", error);
    return null;
  }
}

export async function GetBlobUrlForFile(key) {
  try {
    const workerUrl = `${process.env.REACT_APP_R2_GET_URL}/${key}`;

    let headers = {};
    try {
      const idToken = await getIdToken();
      if (idToken) {
        headers.Authorization = `Bearer ${idToken}`;
      }
    } catch (authError) {
      // No session or user is not logged in — silently continue without auth
    }

    const response = await axios.get(workerUrl, {
      headers,
      responseType: 'blob',
    });

    return URL.createObjectURL(response.data);
  } catch (error) {
    console.error("Error retrieving blob for file:", error);
    return null;
  }
}

export async function GetMetaDataForFile(key) {
  try {
    const workerUrl = `${process.env.REACT_APP_R2_HEAD_URL}/${key}`;

    let headers = {};
    try {
      const idToken = await getIdToken() ;
      if (idToken) {
        headers.Authorization = `Bearer ${idToken}`;
      }
    } catch (authError) {
      // No session or user is not logged in — silently continue without auth
    }

    const response = await axios.head(workerUrl, { headers });

    // Filter and extract all x-amz-meta-* headers
    const metadata = Object.entries(response.headers)
      .filter(([name]) => name.toLowerCase().startsWith("x-amz-meta-"))
      .reduce((acc, [name, value]) => {
        const cleanKey = name.substring("x-amz-meta-".length); // strip prefix
        acc[cleanKey] = value;
        return acc;
      }, {});

    return metadata;
  } catch (error) {
    console.error("Error retrieving file metadata:", error);
    return null;
  }
}

/********************************************************
 * Determines if the specific ECS task is still running
 ********************************************************/
export async function IsTaskRunning(arn) {
  try {
    const apiPath = `/tasks/${encodeURIComponent(arn)}/status`;
    const results = await apiClient.handlePost({
      apiName: "soundshatterapi",
      path: apiPath,
    });

    return results.success && results.data.body === "RUNNING";
  } catch (error) {
    return false;
  }
}

function getZipFileKey(stems) {
  if (!stems || stems.length === 0) return null;

  const zipStem = stems.find((stem) => stem.key.toLowerCase().endsWith(".zip"));
  return zipStem ? zipStem.key : null;
}

export const reduxSeparationObject = (
  separationKey,
  stems,
  sourceKey,
  status,
  taskArn
) => {
  const size = stems.reduce((acc, stem) => acc + stem.size, 0);
  const keyParts = separationKey.split("/");
  const zipKey = getZipFileKey(stems);

  return {
    sourceKey: sourceKey,
    stems: stems,
    method: keyParts[keyParts.length - 2],
    model: keyParts[keyParts.length - 1],
    status: status,
    taskArn: taskArn,
    zipKey: zipKey,
    size: size,
  };
};

export async function UpdatePendingSeparation(file, pendingSeparationKey, dispatch) {
  try {
    const stems = await storageClient.list({ key: `${pendingSeparationKey}/` });

    const filteredStems = stems.map((stem) => ({
      key: stem.key,
      size: stem.size,
    }));

    const separation = reduxSeparationObject(
      pendingSeparationKey,
      filteredStems,
      file.key,
      "Complete",
      null
    );

    dispatch(
      updateSeparation({
        newSeparation: {
          pendingSeparationKey,
          separation,
        },
      })
    );
  } catch (error) {
    console.error(`Failed to update separation for ${pendingSeparationKey}:`, error);
  }
}

export async function getCurrentUserSub() {
  try {
    const session = await fetchAuthSession();

    if (!session) {
      throw new Error('Unable to get session.  User may not be authenticated');
    }

    return session.userSub;
  } catch (error) {
    console.error('Failed to get current user sub:', error);
    return null;
  }
}

export async function getCurrentUserEmail() {
  try {
    const session = await fetchAuthSession();
    const email = session.tokens?.idToken?.payload?.email;

    if (!email) {
      throw new Error("Email not found in session.");
    }

    return email;
  } catch (error) {
    console.error("Failed to retrieve user email:", error);
    throw error;
  }
}

export function areDictionariesEqual(dict1, dict2) {
  const keys1 = Object.keys(dict1);
  const keys2 = Object.keys(dict2);

  // Check if the number of keys is the same
  if (keys1.length !== keys2.length) {
    return false;
  }

  // Check if all keys are the same
  const allKeysEqual = keys1.every((key) => keys2.includes(key));
  if (!allKeysEqual) {
    return false;
  }

  // Check if values for all keys are equal
  const allValuesEqual = keys1.every((key) => dict1[key] === dict2[key]);
  if (!allValuesEqual) {
    return false;
  }

  return true;
}

export async function getLibraryUsage(files) {
  let totalUploadsSize = 0;
  let totalOutputSize = 0;

  for (const fileKey in files) {
    const file = files[fileKey];
    totalUploadsSize += file.size;

    for (const separationKey in file.separations) {
      const separation = file.separations[separationKey];
      totalOutputSize += separation.size;
    }
  }

  return {
    TotalUploadsSize: (totalUploadsSize / MbSize).toFixed(1),
    TotalOutputSize: (totalOutputSize / MbSize).toFixed(1),
    TotalLibrarySize: ((totalUploadsSize + totalOutputSize) / MbSize).toFixed(
      1
    ),
  };
}
